// Components
import DropdownInput from "components/inputs/dropdown"
import ListboxInput from "components/inputs/listbox"
import { ComparisonReport_s } from "components/property/reports/report-styles"
import comparisonService from "data/comparison"
import { parseDate } from "data/lib/dates"
import { local } from 'data/lib/local-json'
// Data
import notificationService from "data/notification"
import propertyService from "data/property"
import propertyReportService from "data/reports-property"
import underwritingService from "data/underwriting"
import _ from 'lodash'
import calcService from "data/calc"

import React, { FC, useEffect, useState } from "react"
// Styles
import { ButtonWithPress_s, Button_s, Label_s } from "styles/app-styles"
import { FormError_s } from "styles/form-styles"
import { InputNumber_s } from "styles/input-styles"
import { DateText_s, ReportForm_s } from "styles/report-styles"

interface TenantDetailComparisonProps {
  property: object
}

function useForceUpdate(){
  const [value, setValue] = useState(0); // integer state
  return () => setValue(value => value + 1); // update the state to force render
}

const TenantDetailComparison: FC<TenantDetailComparisonProps> = (props) => {
  const [begindate, setStartMonth]                  = useState<string|null>(null)
  const [comparisons, setComparisons]               = useState<any>(null)
  const [comparison, setComparison]                 = useState<any>(null)
  const [comparisonProperty, setComparisonProperty] = useState<object|null>(null)
  const [propertyComparison, setPropertyComparison] = useState<object|null>(null)
  const [term, setTerm]                             = useState<number|null>(null)
  const [amount_type, setAmtType]                   = useState<string>("amount")
  const [reports, setReports]                       = useState<string[]>(['occupied_area', 'scheduled_base_rent', 'scheduled_recoveries', 'scheduled_percentage_rent', 'tenant_improvement', 'leasing_commissions']);
  const forceUpdate = useForceUpdate();

  useEffect(() => {
    const property = JSON.parse(JSON.stringify(props['property']))
    if (comparison && propertyComparison?.['sourcepropertyid'] !== property['propertyid'] && property['assetid']){
      getPropertyComparison(comparison, property['assetid'])
    }
  }, [props['property']])

  useEffect(() => {
    const currentFolder = _.last(local('folderPath'))

    const comparisonSubscription = comparisonService.stream().subscribe((data) => {
      if (data.state == 'ok') {
        setComparisons(data.value)
        if (!comparison && data.value.length > 0){
          let comparison = data.value[0]
          setComparison(JSON.parse(JSON.stringify(comparison)))
        }
      }
    })

    comparisonService.fetchComparisonsByFolderId(currentFolder)

    return function cleanup() {
      comparisonSubscription.unsubscribe()
    }

  }, [])

  useEffect(() => { 
    if (comparison){
      let comp = _.find(comparisons, {'comparisonid': Number(comparison)}) || comparison
      setTerm(comp['comparisondetail']?.['term'])
      setStartMonth(comp['comparisondetail']?.['begindate'])
      
      const assetid = props['property']['assetid'];
      if (assetid && comparison){
        getPropertyComparison(comparison, assetid)
      }
    }         
  }, [comparison])

  function getPropertyComparison(comparison:any, assetid:number){
    let comparisonid = typeof(comparison) === 'object' ? comparison['comparisonid'] : comparison
    comparisonService.findPropertyComparison(comparisonid, assetid).then((propertyComparison) => {
      if (propertyComparison){
        setPropertyComparison(propertyComparison)
        if (propertyComparison['comparisonpropertyid']){
          propertyService.fetchProperty(propertyComparison['comparisonpropertyid']).then((property) => {
            setComparisonProperty(property)
          })
        }
      }
    })
  }

  function submit():void {
    let compObj = typeof(comparison) === 'object' ? comparison : _.find(comparisons, {comparisonid: comparison})
    propertyReportService.propertyReport({
      report: 'tenant_detail_comparison',
      params: {
        begindate: underwritingService.toISOdate(begindate),
        term, 
        amount_type,
        reports,
        comparison: {
          comparison_assetid: propertyComparison?.['assetid'],
          comparison_folderid: compObj['comparisonfolderid'],
          comparison_propertyid: propertyComparison?.['comparisonpropertyid'],
          comparison_code: compObj['code'],
          month_offset: 0 
        }
      }
    });
  }

  function updateDate(date:string):void {
    const currentProperty = propertyService.get();
    const analysisBegin1 = currentProperty['analysisbegindate']
    const analysisBegin2 = comparisonProperty?.['analysisbegindate']
    // First make sure date selected is after both properties' analysis begin dates
    if (parseDate(date).isBefore(parseDate(analysisBegin1)) || parseDate(date).isBefore(parseDate(analysisBegin2))){
      date = parseDate(analysisBegin1).isBefore(parseDate(analysisBegin2)) ? analysisBegin2 : analysisBegin1
      notificationService.notify({
        type: "warn",
        message: 'Report Begin Date adjusted to earliest possible date, the Analysis Begin',
        dismiss: 5000
      })
    }
    const maxDate1 = propertyReportService.validateStartDate(currentProperty)[1];
    const maxDate2 = comparisonProperty ? propertyReportService.validateStartDate(comparisonProperty!)[1] : null
    // Next, make sure date chosen is prior to the lesser max date of the comparison properties
    const maxDate = parseDate(maxDate1).isBefore(parseDate(maxDate2)) ? maxDate1 : maxDate2
    if (parseDate(date).isAfter(parseDate(maxDate))){
      date = maxDate;
      notificationService.notify({
        type: "warn",
        message: 'Report Display Date adjusted to the max date',
        dismiss: 5000
      })
    }
    // Validate term against both properties again, update if needed
    let validatedTerm:number|boolean = false    
    if (term){
      let validated1 = propertyReportService.validateTerm(term, date, currentProperty);
      let validated2 = comparisonProperty ? propertyReportService.validateTerm(term, date, comparisonProperty!) : null
      validatedTerm = (validated1 && validated2) ? Math.min(validated1, validated2) : validated1 ? validated1 : validated2 ? validated2 : false  
    }
    setStartMonth(date);
    if (validatedTerm) setTerm(validatedTerm);
    forceUpdate();
  }

  function updateTerm(term:number):void {
    const currentProperty = propertyService.get();
    let validatedTerm:number|boolean = false
    let validated1 = propertyReportService.validateTerm(term, begindate, currentProperty);
    let validated2 = propertyReportService.validateTerm(term, begindate, comparisonProperty!);
    validatedTerm = (validated1 && validated2) ? Math.min(validated1, validated2) : validated1 ? validated1 : validated2 ? validated2 : false
    if (validatedTerm) setTerm(validatedTerm)
    else setTerm(term)
    forceUpdate();
  }

  const disabled = !calcService.calcCheck(props.property) || propertyComparison?.['status'] === 'missing_calc' || propertyComparison?.['status'] === 'property_not_found'
  const missingCalc1 = !calcService.calcCheck(props.property)
  const missingCalc2 = propertyComparison?.['status'] === 'missing_calc'
  const propNotFound = propertyComparison?.['status'] === 'property_not_found'

  const reportsOptions = [
    {label: 'Occupied Area', value: 'occupied_area'},
    {label: 'Vacant Area', value: 'vacant_area'},
    {label: 'Market Rent', value: 'market_rent'},
    {label: 'Market Recoveries', value: 'market_recoveries'},
    {label: 'Potential Base Rent', value: 'base_rent'},
    {label: 'Base Rent - Vacancy Loss', value: 'base_rent_vacancy_loss'},
    {label: 'Base Rent - Abatement', value: 'base_rent_abatement'},
    {label: 'Scheduled Base Rent', value: 'scheduled_base_rent'},
    {label: 'Potential Recoveries', value: 'recoveries'},
    {label: 'Recoveries - Vacancy Loss', value: 'recoveries_vacancy_loss'},
    {label: 'Recoveries - Abatement', value: 'recoveries_abatement'},
    {label: 'Scheduled Recoveries', value: 'scheduled_recoveries'},
    {label: 'Potential Misc Revenue', value: 'misc_revenue'},
    {label: 'Misc Revenue - Vacancy Loss', value: 'misc_revenue_vacancy_loss'},
    {label: 'Misc Revenue - Abatement', value: 'misc_revenue_abatement'},
    {label: 'Scheduled Misc Revenue', value: 'scheduled_misc_revenue'},
    {label: 'Potential Percentage Rent', value: 'percentage_rent'},
    {label: 'Percentage Rent - Vacancy Loss', value: 'percentage_rent_vacancy_loss'},
    {label: 'Percentage Rent - Abatement', value: 'percentage_rent_abatement'},
    {label: 'Scheduled Percentage Rent', value: 'scheduled_percentage_rent'},
    {label: 'Tenant Improvement', value: 'tenant_improvement'},
    {label: 'Leasing Commissions', value: 'leasing_commissions'},
    {label: 'Other Tenant Capital', value: 'other_tenant_capital'},
    {label: 'Sales Volume', value: 'sales_volume'}
  ];

  return (
    <ComparisonReport_s>
      <ButtonWithPress_s primary onClick={submit} style={{marginBottom: 20}} disabled={disabled}>Run Report</ButtonWithPress_s>
      <ReportForm_s>
        { missingCalc1 && <FormError_s style={{marginRight: '5px'}}>Current property is not calculated</FormError_s> }
        { missingCalc2 && <FormError_s style={{marginRight: '5px'}}>Comparison property is not calculated</FormError_s> }
        { propNotFound && <FormError_s>Property not found for comparison</FormError_s> }
      </ReportForm_s>
      <ReportForm_s>
        <div>
          <Label_s>Comparison</Label_s>
          <DropdownInput
            className="comparison-input"
            persist={(field:string, val:string)=>setComparison(val)}
            field="comparison"
            options={comparisons?.map(comparison => { return {'label': `${comparison['code']}`, 'value': comparison['comparisonid']}})}
            value={comparison}
          />
        </div>
        <div>
          <Label_s>Start Date</Label_s>
          <DateText_s 
            withoutDays 
            value={parseDate(begindate).format('MM/DD/YYYY')} 
            onChange={updateDate} 
            onBlur={updateDate} 
            persist={updateDate} 
          />
        </div>
        <div>
          <Label_s>Term</Label_s>
          <InputNumber_s 
            value={term} 
            onChange={(e) => updateTerm(e.value)} 
            maxFractionDigits={0} 
          />
        </div>
        <div>
          <Label_s>Amount Type</Label_s>
          <DropdownInput
            persist={(field:string, val:string)=>setAmtType(val)}
            field="amount_type"
            options={[{label: 'Amount', value: 'amount'},{label: 'Per Area', value: 'per_area'}]}
            value={amount_type}
          />
        </div>
      </ReportForm_s>
      <ReportForm_s>
        <div style={{width: '250px'}}>
          <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10}}>
            <Label_s>Tenant Detail Type</Label_s>
            <Button_s onClick={()=>{ reports.length === reportsOptions.length ? setReports([]) : setReports(reportsOptions.map(option => option.value))}}>
              {reports.length === reportsOptions.length ? 'Deselect All' : 'Select All'}
            </Button_s>
          </div>
          <ListboxInput 
            field="reports" 
            value={reports} 
            options={reportsOptions} 
            persist={(field:string, val:string[])=>setReports(val)} 
            multiple={true} 
          />
        </div>
      </ReportForm_s>
    </ComparisonReport_s> ) 
}

export default TenantDetailComparison
